/** The parts of the Screen Wake Lock API used here (typed locally: iOS < 16.4 and older lib.dom have no `wakeLock`). */
export interface WakeLockSentinelLike {
  readonly released?: boolean;
  release(): Promise<void>;
  addEventListener?(type: "release", listener: () => void): void;
}
export interface WakeLockNavigator { readonly wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> } }
export interface WakeLockDocument {
  readonly hidden: boolean;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

/**
 * Keeps the phone's screen on while a level runs (auto-lock after 30 s of no touches otherwise dims and locks it
 * mid-wave, since a battle is watched more than touched). `hold(true)` when the sim runs, `hold(false)` on pause, level
 * end, quit and the hub.
 *
 * The browser drops the lock whenever the page is hidden; coming back visible asks again if it is still wanted.
 * Everything is feature-detected and every failure swallowed: no API (iOS < 16.4, desktop Firefox), a refusal (low
 * power mode, no user activation yet) or a throw simply means the screen may lock as it always did.
 */
export class ScreenWakeLock {
  private wanted = false;
  private sentinel: WakeLockSentinelLike | null = null;
  private pending = false;

  constructor(
    private readonly nav: WakeLockNavigator | undefined = typeof navigator === "undefined" ? undefined : (navigator as WakeLockNavigator),
    private readonly doc: WakeLockDocument | undefined = typeof document === "undefined" ? undefined : document,
  ) {
    doc?.addEventListener("visibilitychange", this.onVisibility);
  }

  get supported(): boolean { return typeof this.nav?.wakeLock?.request === "function"; }
  /** Whether a lock is held right now (for tests and the debug readout). */
  get held(): boolean { return this.sentinel !== null && this.sentinel.released !== true; }

  hold(on: boolean): void {
    this.wanted = on;
    if (on) this.acquire();
    else this.drop();
  }

  /**
   * Asks again if a lock is wanted but not held — called from a user gesture, since a browser may refuse a request
   * made without one (the level starts behind the cloud wipe, after the PLAY tap's activation has lapsed).
   */
  retry(): void { if (this.wanted) this.acquire(); }

  dispose(): void {
    this.hold(false);
    this.doc?.removeEventListener("visibilitychange", this.onVisibility);
  }

  private readonly onVisibility = (): void => {
    if (this.doc?.hidden) return;
    // the system released it while hidden (the sentinel's release event may or may not have fired first)
    if (this.sentinel?.released) this.sentinel = null;
    if (this.wanted) this.acquire();
  };

  private acquire(): void {
    if (!this.supported || this.pending || this.held || this.doc?.hidden) return;
    this.pending = true;
    let request: Promise<WakeLockSentinelLike>;
    try { request = this.nav!.wakeLock!.request("screen"); } catch { this.pending = false; return; }
    request.then((s) => {
      this.pending = false;
      if (!this.wanted) { s.release().catch(() => undefined); return; }   // let go while the request was in flight
      this.sentinel = s;
      s.addEventListener?.("release", () => { if (this.sentinel === s) this.sentinel = null; });
    }, () => { this.pending = false; });
  }

  private drop(): void {
    const s = this.sentinel;
    this.sentinel = null;
    if (s && !s.released) { try { s.release().catch(() => undefined); } catch { /* already gone */ } }
  }
}
